import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, KeyRound, X } from 'lucide-react';
import { LogoMark } from '@/components/Logo';
import clsx from 'clsx';
import { PASSWORD_MIN_LENGTH, type AuthenticatedResponse } from '@nlinks/shared';
import { api, ApiError } from '@/lib/api';
import { homeFor } from '@/App';
import { useAuth } from '@/store/auth';
import { Alert, Button, Field, Input } from '@/components/ui';

interface Rule {
  label: string;
  test: (value: string) => boolean;
}

const RULES: Rule[] = [
  { label: `At least ${PASSWORD_MIN_LENGTH} characters`, test: (v) => v.length >= PASSWORD_MIN_LENGTH },
  { label: 'One lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'One digit', test: (v) => /\d/.test(v) },
];

/**
 * Mandatory stop after first login (and after any admin-initiated reset).
 * Mirrors the server-side policy exactly so the user never gets a rejection
 * they couldn't see coming.
 */
export function ChangePasswordPage() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const results = useMemo(() => RULES.map((rule) => ({ ...rule, passed: rule.test(newPassword) })), [newPassword]);
  const allPassed = results.every((r) => r.passed);
  const matches = newPassword.length > 0 && newPassword === confirmPassword;
  const isReused = newPassword.length > 0 && newPassword === currentPassword;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // The server ends every other session and returns a fresh pair for this
      // device, so we swap the whole auth state rather than just navigating.
      const response = await api.post<AuthenticatedResponse>('/api/auth/change-password', {
        currentPassword,
        newPassword,
        confirmPassword,
      });
      signIn(response);
      /**
       * Only end users have an onboarding step, because completing it means
       * connecting a mailbox. Administrators own no mailboxes, so their
       * `onboardingCompleted` is never true — branching on that flag alone sent
       * every new admin into "connect a mailbox" immediately after setting
       * their password, with no way past it.
       */
      const isStaff = response.user.role === 'ADMIN' || response.user.role === 'SUPER_ADMIN';
      const next = isStaff || response.user.onboardingCompleted ? homeFor(response.user.role) : '/onboarding';
      navigate(next, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update your password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <LogoMark className="mb-4 h-14 w-14" />
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">Choose your password</h1>
          <p className="mt-1.5 text-sm text-slate-500">
            {user?.fullName ? `Welcome, ${user.fullName.split(' ')[0]}. ` : ''}
            Replace the temporary password before continuing.
          </p>
        </div>

        <form onSubmit={submit} className="card animate-slide-up space-y-5 p-6 sm:p-8">
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Field label="Temporary password" htmlFor="current" required>
            <Input
              id="current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              required
            />
          </Field>

          <Field label="New password" htmlFor="new" required error={isReused ? 'Choose something different from your temporary password' : undefined}>
            <Input
              id="new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>

          <ul className="space-y-1.5 rounded-lg bg-surface-base/60 p-3">
            {results.map((rule) => (
              <li key={rule.label} className="flex items-center gap-2 text-xs">
                {rule.passed ? (
                  <Check className="h-3.5 w-3.5 shrink-0 text-emerald-400" aria-hidden />
                ) : (
                  <X className="h-3.5 w-3.5 shrink-0 text-slate-600" aria-hidden />
                )}
                <span className={clsx(rule.passed ? 'text-slate-300' : 'text-slate-500')}>{rule.label}</span>
              </li>
            ))}
          </ul>

          <Field
            label="Confirm new password"
            htmlFor="confirm"
            required
            error={confirmPassword.length > 0 && !matches ? 'Passwords do not match' : undefined}
          >
            <Input
              id="confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>

          <Button
            type="submit"
            loading={busy}
            disabled={!allPassed || !matches || isReused || currentPassword.length === 0}
            className="w-full"
            size="lg"
            icon={<KeyRound className="h-4 w-4" />}
          >
            Set password and continue
          </Button>

          <p className="text-center text-xs text-slate-600">
            Any other devices signed in to this account will be signed out.
          </p>
        </form>
      </div>
    </div>
  );
}
